import { BI_INTEGRATIONS } from '@elementary/common-ui/constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BiToolForm,
  FetchBiIntegrationApi,
  VersionControl,
} from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { clearSecretIndication } from 'shared/utils/secretUtils';
import useSnackbar from '../useSnackbar';
import useFlags from '../useFlags';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type FetchBiIntegrationsApi = {
  destinations: FetchBiIntegrationApi[];
};

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useBiIntegration = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { hasFlag } = useFlags();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const biIntegrationsQuery = useQuery(
    [BI_INTEGRATIONS, envId],
    () => api.fetchBiIntegrations(envId!),
    {
      enabled: !!envId && hasFlag('bi'),
      select: (data: FetchBiIntegrationsApi): FetchBiIntegrationApi[] =>
        data.destinations || [],
    },
  );

  const onBiIntegrationSuccess = () => {
    onUpdateSuccess?.();
    queryClient.invalidateQueries([BI_INTEGRATIONS, envId]);
    openSnackbar({
      message: 'Successfully connected to your BI tool',
      variant: 'success',
    });
    tracking?.sendTrackingEvent({
      eventName: trackingEvents?.CONNECT_BI_TOOL_INTEGRATION_SUCCESSFULLY,
    });
  };

  const onBiIntegrationError = (error: MutationError) => {
    sendTrackingWithoutSensitiveData({
      error,
      eventName: trackingEvents?.CONNECT_BI_TOOL_INTEGRATION_FAILURE,
    });
  };

  const biToolMutation = useMutation(
    ({ data, mode }: { data: BiToolForm; mode: 'update' | 'create' }) => {
      let reqData = {
        name: '',
        bi_type: data.selectedBiTool,
        enabled: true,
        bi_settings: {},
      };

      switch (data.selectedBiTool) {
        case 'Tableau':
          reqData = {
            bi_type: 'Tableau',
            name: data?.tableauConnectionName?.trim(),
            enabled: true,
            bi_settings: {
              personal_access_token_name:
                data?.tableauPersonalAccessTokenName?.trim(),
              personal_access_token_value: clearSecretIndication(
                data?.tableauPersonalAccessTokenValue,
              )?.trim(),
              server_url: data?.tableauServerUrl?.trim(),
              site_name: data?.tableauSiteName?.trim(),
            },
          };
          break;
        case 'Looker':
          reqData = {
            bi_type: 'Looker',
            enabled: true,
            name: data.lookerConnectionName?.trim(),
            bi_settings: {
              server_url: data.lookerServerUrl?.trim(),
              client_id: data.lookerClientId?.trim(),
              client_secret: clearSecretIndication(
                data.lookerClientSecret,
              )?.trim(),
              git_provider: data.lookerGitProvider?.trim() as VersionControl,
              git_token: clearSecretIndication(data.lookerGitToken)?.trim(),
              repo_name: data.lookerRepoName?.trim(),
            },
          };
          break;
        case 'PowerBI':
          reqData = {
            bi_type: 'PowerBI',
            name: data.powerBiConnectionName,
            enabled: true,
            bi_settings: {
              tenant: data.powerBiTenant?.trim(),
              client_id: data.powerBiClientId?.trim(),
              client_secret: clearSecretIndication(
                data.powerBiClientSecret,
              )?.trim(),
              workspaces: data.powerBiWorkspaces,
            },
          };
          break;
        case 'Sigma':
          reqData = {
            bi_type: 'Sigma',
            name: data.sigmaConnectionName?.trim(),
            enabled: true,
            bi_settings: {
              cloud_provider: data.sigmaCloudProvider,
              client_id: data.sigmaClientId?.trim(),
              client_secret: clearSecretIndication(
                data.sigmaClientSecret,
              )?.trim(),
            },
          };
          break;
        case 'ThoughtSpot':
          reqData = {
            bi_type: 'ThoughtSpot',
            name: data.thoughtSpotConnectionName?.trim(),
            enabled: true,
            bi_settings: {
              username: data.thoughtSpotUsername?.trim(),
              secret_key: clearSecretIndication(
                data.thoughtSpotSecretKey,
              )?.trim(),
              base_url: data.thoughtSpotBaseUrl?.trim(),
            },
          };
          break;
        case 'Hex':
          reqData = {
            bi_type: 'Hex',
            name: data.hexConnectionName?.trim(),
            enabled: true,
            bi_settings: {
              workspace_token: clearSecretIndication(
                data.hexWorkspaceToken,
              )?.trim(),
              custom_url: data.hexCustomUrl?.trim() || null,
            },
          };
          break;
        case 'Lightdash':
          reqData = {
            bi_type: 'Lightdash',
            name: data.lightdashConnectionName?.trim(),
            enabled: true,
            bi_settings: {
              host_url: data.lightdashHostUrl?.trim() || undefined,
              api_token: clearSecretIndication(data.lightdashApiToken)?.trim(),
              project_uuids: data.lightdashProjectUuids?.filter(Boolean),
            },
          };
          break;
        default:
          break;
      }

      return mode === 'update'
        ? api.updateBiIntegration({ data: reqData, id: data.id, envId: envId! })
        : api.setBiIntegration({ data: reqData, envId: envId! });
    },
    {
      onSuccess: onBiIntegrationSuccess,
      onError: onBiIntegrationError,
    },
  );

  const deleteBiIntegrationMutation = useMutation(
    (id: string) => api.deleteBiIntegration({ id, envId: envId! }),
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([BI_INTEGRATIONS, envId]);
        openSnackbar({
          message: 'Successfully disconnected from your BI tool',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName:
            trackingEvents?.DISCONNECT_BI_TOOL_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) => {
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.DISCONNECT_BI_TOOL_INTEGRATION_FAILURE,
        });
        openSnackbar({
          message: 'Failed to disconnect from your BI tool',
          variant: 'error',
        });
      },
    },
  );

  const syncNowBiIntegrationMutation = useMutation(
    (id: string) => api.syncNowBiIntegration({ id, envId: envId! }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([BI_INTEGRATIONS, envId]);
        openSnackbar({
          message: 'BI sync triggered successfully',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to trigger BI sync',
          variant: 'error',
        });
      },
    },
  );

  const isBiToolConnected =
    !biIntegrationsQuery.isLoading &&
    !!biIntegrationsQuery.data?.length &&
    biIntegrationsQuery.data.every(
      (integration) =>
        integration.last_sync_status === 'success' ||
        integration.last_sync_status === null, // just connected, no sync yet
    );

  return {
    biIntegrationsQuery,
    biToolMutation,
    deleteBiIntegrationMutation,
    syncNowBiIntegrationMutation,
    isBiToolConnected,
  };
};

export default useBiIntegration;
